import classNames from "classnames";
import React, { FocusEvent, useMemo } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { NumericFormat } from "react-number-format";

import { useAppSelector } from "../../redux/Store";
import { ITRFilingSelector } from "../../redux/slice/ITRFilingSlice";

type Props = {
  name: string;
  value: string | number | undefined;
  onBlur: (e: FocusEvent<HTMLInputElement>) => void;
  onChange: (value: string) => void;
  isInvalid: boolean | undefined;
  errorMsg?: string | undefined;
  size?: "lg" | "sm" | undefined;
  placeholder?: string;
  decimalUpto?: number;
  classes?: any;
  isDisabled?: boolean;
  isRequired?: boolean;
  showLeftIcon?: boolean;
  leftIcon?: string;
  showRightIcon?: boolean;
  rightIcon?: string;
  separator?: string;
  label?: string;
  maxLength?: number;
  groupStyle?: "lakh" | "thousand";
  note?: JSX.Element | string;
  allowNegative?: boolean;
  groupTextClass?: string;
  formType?: "ITR" | "GENERAL";
};

const NumericInputField: React.FC<Props> = (props: Props) => {
  const {
    name,
    value,
    onBlur,
    onChange,
    isInvalid,
    errorMsg,
    size,
    placeholder = "",
    decimalUpto = 2,
    classes,
    isDisabled = false,
    isRequired = false,
    separator = ",",
    showLeftIcon = false,
    leftIcon = "₹",
    showRightIcon = false,
    rightIcon = "%",
    label = "",
    maxLength,
    groupStyle = "lakh",
    note,
    allowNegative = false,
    groupTextClass,
    formType = "ITR"
  } = props;

  const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

  const isFieldDisabled = useMemo(() => {
    if (formType === "GENERAL") {
      if (isDisabled) {
        return isDisabled;
      }
      return false;
    }
    if (isFilingReadOnly) {
      return true;
    }
    return isDisabled;
  }, [isDisabled, isFilingReadOnly, formType]);

  return (
    <>
      <Form.Group className="form-group" controlId={name}>
        {label ? (
          <Form.Label className={classNames("small-label", isRequired && "required-field")}>
            {label}
          </Form.Label>
        ) : null}
        <InputGroup className={classNames(isFieldDisabled && "is-disabled")}>
          {showLeftIcon && (
            <InputGroup.Text
              className={classNames("prepend-text", groupTextClass, isInvalid && "border-danger")}
            >
              {leftIcon}
            </InputGroup.Text>
          )}
          <NumericFormat
            className={classNames(
              "form-control",
              size === "lg" && "form-control-lg",
              size === "sm" && "form-control-sm",
              classes,
              isInvalid && "border-danger"
            )}
            disabled={isFieldDisabled}
            name={name}
            value={value}
            onBlur={onBlur}
            autoComplete="false"
            placeholder={placeholder}
            maxLength={maxLength}
            onValueChange={(_values) => onChange(_values.value)}
            allowLeadingZeros={false}
            allowNegative={allowNegative}
            decimalScale={decimalUpto}
            thousandSeparator={separator}
            thousandsGroupStyle={groupStyle}
          />
          {showRightIcon && (
            <InputGroup.Text
              className={classNames("append-text", groupTextClass, isInvalid && "border-danger")}
            >
              {rightIcon}
            </InputGroup.Text>
          )}
        </InputGroup>
        {note && !isInvalid && !errorMsg && <Form.Text className="field-text">{note}</Form.Text>}
        {isInvalid && errorMsg && <div className="field-error">{errorMsg}</div>}
      </Form.Group>
    </>
  );
};

export default NumericInputField;
